import React, { useState } from 'react';
import { MoreHorizontal, ChevronDown, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '../ui/alert-dialog';

/**
 * Reusable, keyboard-accessible row actions menu for admin tables.
 * Replaces crowded rows of icon-only buttons with a single labelled "Manage" button.
 *
 * groups: Array<{
 *   label?: string,          // section header
 *   note?: string,           // small warning/help line under the header
 *   items: Array<{
 *     label: string,
 *     onClick: () => void,
 *     icon?: React.ReactNode,
 *     helper?: string,       // small muted second line
 *     destructive?: boolean, // red styling
 *     disabled?: boolean,
 *     testId?: string,
 *     confirm?: { title, description, actionLabel?, cancelLabel? }
 *   }>
 * }>
 */
export const RowActionsMenu = ({ groups = [], label = 'Manage', triggerTestId, align = 'end' }) => {
  const [confirmItem, setConfirmItem] = useState(null);

  const visibleGroups = groups.filter((g) => g.items && g.items.length > 0);
  if (visibleGroups.length === 0) return null;

  const runItem = (item) => {
    if (item.confirm) {
      setConfirmItem(item);
    } else if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1 whitespace-nowrap"
            aria-label={label}
            data-testid={triggerTestId}
          >
            <MoreHorizontal className="w-4 h-4 sm:hidden" />
            <span className="hidden sm:inline">{label}</span>
            <ChevronDown className="w-3.5 h-3.5 hidden sm:inline" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-60">
          {visibleGroups.map((group, gi) => (
            <React.Fragment key={gi}>
              {gi > 0 && <DropdownMenuSeparator />}
              {group.label && (
                <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">
                  {group.label}
                </DropdownMenuLabel>
              )}
              {group.note && (
                <div className="px-2 pb-1.5 pt-0.5 text-[11px] leading-snug text-amber-600 flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{group.note}</span>
                </div>
              )}
              {group.items.map((item, ii) => (
                <DropdownMenuItem
                  key={ii}
                  disabled={item.disabled}
                  onSelect={() => {
                    // Let the menu close first, then run (prevents Radix pointer-events lock when opening a dialog).
                    setTimeout(() => runItem(item), 0);
                  }}
                  data-testid={item.testId}
                  className={item.destructive ? 'text-red-600 focus:text-red-700 focus:bg-red-50' : ''}
                >
                  {item.icon && <span className="mr-2 flex-shrink-0">{item.icon}</span>}
                  <span className="flex flex-col">
                    <span className="text-sm">{item.label}</span>
                    {item.helper && (
                      <span className="text-[11px] leading-snug text-slate-400">{item.helper}</span>
                    )}
                  </span>
                </DropdownMenuItem>
              ))}
            </React.Fragment>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirmItem} onOpenChange={(open) => !open && setConfirmItem(null)}>
        <AlertDialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmItem?.confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmItem?.confirm?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{confirmItem?.confirm?.cancelLabel || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const item = confirmItem;
                setConfirmItem(null);
                item?.onClick?.();
              }}
              className={confirmItem?.destructive ? 'bg-red-600 hover:bg-red-700' : ''}
              data-testid={confirmItem?.testId ? `${confirmItem.testId}-confirm` : undefined}
            >
              {confirmItem?.confirm?.actionLabel || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default RowActionsMenu;
